import React from 'react';
import DefaultLayout from '../../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../../layouts/TemplateColumn';
import { PageHeader, PageSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, TextBlock, SecondaryCallout} from '../../../components';
import Link from 'gatsby-link';
import GlobalSafetyData, { globalSafetyBlock } from './_global_safety_data'
import { jobCodes } from '../../../util/data/jobCodes';

// SEO INFORMATION
const pageMeta = {
    title: `Adverse Reactions for First-line Combination Therapy for Squamous mNSCLC`,
    keywords: `keytruda adverse reactions first-line combination therapy for squamous mnsclc`,
    description: `Learn about the immune-mediated adverse reactions for KEYTRUDA® (pembrolizumab) and the most common adverse reactions associated with the KEYNOTE-407 clinical trial.`,
    schemaJsonLD: [
        `{"@context":"http://schema.org/","url":"https://www.keytrudahcp.com/safety/adverse-reactions/nsclc-squamous-first-line-combination-therapy/","@type":"MedicalWebPage","name":"Most Common Adverse Reactions in KEYNOTE⁠-⁠407","description":"In KEYNOTE⁠-⁠407, when KEYTRUDA was administered with carboplatin and either paclitaxel or paclitaxel protein‑bound in metastatic squamous NSCLC, KEYTRUDA was discontinued due to adverse reactions in 15% of 101 patients. The most frequent serious adverse reactions reported in at least 2% of patients were febrile neutropenia, pneumonia, and urinary tract infection. Adverse reactions observed in KEYNOTE⁠-⁠407 were similar to those observed in KEYNOTE⁠-⁠189 with the exception that increased incidences of alopecia (47% vs 36%) and peripheral neuropathy (31% vs 25%) were observed in the KEYTRUDA and chemotherapy arm compared to the placebo and chemotherapy arm in KEYNOTE⁠-⁠407."}`,
        `{"@context":"http://schema.org/","url":"https://www.keytrudahcp.com/safety/adverse-reactions/nsclc-squamous-first-line-combination-therapy/","@type":"MedicalAudience","audienceType":"health care professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`
    ]
}

//from /src/util/data/indications.js
const indicationId = 5;
const jobCode = jobCodes[4].jobCode;

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections : [
        {
            heading: "Additional Safety Data",
            items: [
                { text: '1L Combo Therapy – Nonsquamous', url: '/safety/adverse-reactions/nsclc-nonsquamous-first-line-combination-therapy/' },
                { text: '1L Monotherapy – Nonsquamous & Squamous', url: '/safety/adverse-reactions/nsclc-first-line-monotherapy/' },
                { text: '2L Monotherapy – Nonsquamous & Squamous', url: '/safety/adverse-reactions/nsclc-second-line-monotherapy/' },
                { text: 'Neoadjuvant Combo Therapy Followed by Adjuvant Monotherapy After Surgery for Resectable (Tumors ≥4 cm or Node Positive) NSCLC', url: '/safety/adverse-reactions/nsclc-neoadjuvant-therapy/' },
                { text: 'Adjuvant Therapy for Stage IB (T2a ≥4 cm), Stage II, or Stage IIIA NSCLC', url: '/safety/adverse-reactions/nsclc-adjuvant-therapy/' },
                ]
        },
        {
            heading: "Related Information",
            items: [
                { text: 'Treatment Monitoring & Management', url: '/safety/monitoring-managing-adverse-reactions/' },
                { text: 'Dose Modifications', url: '/dosing/dose-modifications/' },
                ]
        },
    ],
    interestLinks : {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};

const anchorLinkModelData = [
    {label: 'Incidence of Selected Immune-Mediated Adverse Reactions'},
    {label: 'Management of Selected Immune-Mediated Adverse Reactions'},
    {label: 'Most Common Adverse Reactions in KEYNOTE&#8288;-&#8288;407'} 
]

const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={ pageMeta } location={ location }>
            <div data-template-name="template-e">
                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Selected Adverse Reactions" />
                   <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId}/>
                   </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        <PageSection bgColor="cloud" title="Severe and Fatal Immune-Mediated Adverse Reactions">
                            <TextBlock>{globalSafetyBlock}</TextBlock>
                            <AnchorLinks items={anchorLinkModelData} />
                        </PageSection>

                        <GlobalSafetyData />

                        <PageSection title={anchorLinkModelData[2].label}>
                            <TextBlock>
                                <p><b>KEYTRUDA was discontinued due to adverse reactions in 15% of 101 patients.</b> The most frequent serious adverse reactions reported in at least 2% of patients were febrile neutropenia (6%), pneumonia (6%), and urinary tract infection (3%).</p>
                                <p>Adverse reactions observed in KEYNOTE&#8288;-&#8288;407 were similar to those observed in <Link to="/safety/adverse-reactions/nsclc-nonsquamous-first-line-combination-therapy/">KEYNOTE&#8288;-&#8288;189</Link> with the exception that increased incidences of alopecia (47% vs 36%) and peripheral neuropathy (31% vs 25%) were observed in the KEYTRUDA and chemotherapy arm compared to the placebo and chemotherapy arm in KEYNOTE&#8288;-&#8288;407.</p>
                            </TextBlock>
                            <SecondaryCallout 
                                title="85% of patients remained on treatment with KEYTRUDA without discontinuation due to adverse reactions"
                                alt=""    
                            />
                        </PageSection>
                    </TemplateColumn>
                {/* End Main Column */}

                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>

                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent}/>
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;
